/**
 * Mixed-cart warning modal.
 *
 * Detects when a cart contains both preorder and in-stock items
 * (via data-stock-type attributes set by Liquid from metafields)
 * and shows a warning modal. Listens for cart:updated events so
 * it re-evaluates after every Ajax cart change.
 */

/* ── Custom element: <preorder-alert> ────────────────────────── */

if (!customElements.get('preorder-alert')) {
  customElements.define(
    'preorder-alert',
    class PreorderAlert extends HTMLElement {
      constructor() {
        super();
        this.handleOverlayClick = this.handleOverlayClick.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
      }

      connectedCallback() {
        this.overlay = this.querySelector('.preorder-alert__overlay');
        this.dismissBtn = this.querySelector('[data-action="dismiss"]');
        this.viewCartBtn = this.querySelector('[data-action="view-cart"]');

        if (this.overlay) this.overlay.addEventListener('click', this.handleOverlayClick);
        if (this.dismissBtn) this.dismissBtn.addEventListener('click', () => this.dismiss());
        if (this.viewCartBtn) this.viewCartBtn.addEventListener('click', () => this.viewCart());
      }

      disconnectedCallback() {
        if (this.overlay) this.overlay.removeEventListener('click', this.handleOverlayClick);
        document.removeEventListener('keydown', this.handleKeyDown);
      }

      /* ── Public API ──────────────────────────────────────────── */

      show() {
        if (this.hasAttribute('open')) return;
        this.setAttribute('open', '');
        this.removeAttribute('hidden');
        document.body.classList.add('preorder-alert-open');
        document.addEventListener('keydown', this.handleKeyDown);
        this.trapFocus();
      }

      hide() {
        this.removeAttribute('open');
        this.setAttribute('hidden', '');
        document.body.classList.remove('preorder-alert-open');
        document.removeEventListener('keydown', this.handleKeyDown);
        if (this._previouslyFocused) {
          this._previouslyFocused.focus();
          this._previouslyFocused = null;
        }
      }

      dismiss() {
        this.hide();
        if (window._mixedCartDetector) window._mixedCartDetector.onDismiss();
      }

      viewCart() {
        this.hide();
        if (window._mixedCartDetector) window._mixedCartDetector.onDismiss();

        const cartDrawer = document.getElementById('CartDrawer');
        if (cartDrawer && typeof cartDrawer.show === 'function') {
          cartDrawer.show();
          return;
        }

        const mainCart = document.getElementById('MainContent');
        if (window.location.pathname === '/cart' && mainCart) {
          mainCart.scrollIntoView({ behavior: 'smooth' });
          return;
        }

        window.location.href = '/cart';
      }

      /* ── Focus trap ──────────────────────────────────────────── */

      trapFocus() {
        this._previouslyFocused = document.activeElement;
        const focusable = this.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable.length) return;

        this._firstFocusable = focusable[0];
        this._lastFocusable = focusable[focusable.length - 1];
        this._firstFocusable.focus();
      }

      /* ── Private handlers ────────────────────────────────────── */

      handleOverlayClick(e) {
        if (e.target === this.overlay) this.dismiss();
      }

      handleKeyDown(e) {
        if (e.key === 'Escape') {
          this.dismiss();
          return;
        }
        if (e.key !== 'Tab') return;

        if (e.shiftKey) {
          if (document.activeElement === this._firstFocusable) {
            e.preventDefault();
            this._lastFocusable.focus();
          }
        } else {
          if (document.activeElement === this._lastFocusable) {
            e.preventDefault();
            this._firstFocusable.focus();
          }
        }
      }
    }
  );
}

/* ── Mixed-cart detector ─────────────────────────────────────── */

class MixedCartDetector {
  static STORAGE_KEY = 'preorder_alert_dismissed';

  constructor() {
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.check());
    } else {
      this.check();
    }

    document.addEventListener('cart:updated', () => {
      requestAnimationFrame(() => this.check());
    });

    document.addEventListener('cart:refresh', () => {
      setTimeout(() => this.check(), 600);
    });

    document.addEventListener('ajaxProduct:added', () => {
      setTimeout(() => this.check(), 800);
    });
  }

  isDismissed() {
    try {
      return sessionStorage.getItem(MixedCartDetector.STORAGE_KEY) === '1';
    } catch { return false; }
  }

  onDismiss() {
    try {
      sessionStorage.setItem(MixedCartDetector.STORAGE_KEY, '1');
    } catch { /* private browsing may block storage */ }
  }

  check() {
    if (this.isDismissed()) return;

    const stockTypes = this.getStockTypes();
    const isMixed = this.isMixed(stockTypes);

    const alert = document.getElementById('PreorderAlert');
    if (!alert) return;

    if (isMixed) {
      alert.show();
    }
  }

  /**
   * Reads data-stock-type from cart line items only — scoped to
   * <cart-items> elements so product cards on collection pages
   * are excluded.
   */
  getStockTypes() {
    const els = document.querySelectorAll('cart-items [data-stock-type]');
    if (els.length) {
      const types = [];
      els.forEach(el => {
        const val = (el.dataset.stockType || '').trim().toLowerCase();
        if (val) types.push(val);
      });
      return types;
    }
    return [];
  }

  /**
   * Returns true when the cart contains at least one preorder
   * AND at least one in-stock item.
   */
  isMixed(types) {
    if (types.length < 2) return false;
    let hasPreorder = false;
    let hasInStock = false;
    for (const t of types) {
      if (t === 'preorder' || t === 'pre-order' || t === 'pre order') {
        hasPreorder = true;
      } else {
        hasInStock = true;
      }
      if (hasPreorder && hasInStock) return true;
    }
    return false;
  }
}

window._mixedCartDetector = new MixedCartDetector();
